import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { salaryAPI, organizationAPI } from '@/lib/api';
import { toast } from 'sonner';
import {
  Download, Search, Users, Banknote, TrendingDown, Calendar, Loader2,
} from 'lucide-react';

const fmt = (amount) => {
  if (amount == null || isNaN(amount)) return '0.00';
  return Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function KPICard({ icon, label, value, color }) {
  return (
    <Card>
      <CardContent className="py-4 px-5 flex items-center gap-4">
        <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${color}`}>
          {icon}
        </div>
        <div>
          <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</p>
          <p className="text-lg font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status, isFinalized, isPaid }) {
  if (status === 'exited') {
    return <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-gray-200 text-gray-700">Exited</span>;
  }
  if (isFinalized) {
    return <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-emerald-100 text-emerald-700">Finalized</span>;
  }
  return <span className="px-2 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-orange-100 text-orange-700">On Notice</span>;
}

function ReasonBadge({ reason }) {
  if (!reason) return <span className="text-xs text-muted-foreground">—</span>;
  const colors = reason === 'termination'
    ? 'bg-red-50 text-red-600 border-red-200'
    : 'bg-blue-50 text-blue-600 border-blue-200';
  return <span className={`px-2 py-0.5 text-[10px] font-medium rounded-full border ${colors} capitalize`}>{reason}</span>;
}

function HistoryTableRow({ row }) {
  const isNegative = row.bank_transfer < 0;
  return (
    <tr className="border-b hover:bg-muted/30 transition-colors text-sm">
      <td className="py-3 px-4">
        <div className="font-medium">{row.name}</div>
        <div className="text-xs text-muted-foreground">{row.emp_code}</div>
      </td>
      <td className="py-3 px-4 text-xs text-muted-foreground">{row.organization}</td>
      <td className="py-3 px-4 text-xs">{row.notice_date || '—'}</td>
      <td className="py-3 px-4 text-xs">{row.lwd || '—'}</td>
      <td className="py-3 px-4 text-center text-xs">
        {row.days_worked}/{row.total_days}
        {row.months_count > 1 && (
          <div className="text-[10px] text-muted-foreground">({row.months_count} months)</div>
        )}
      </td>
      <td className="py-3 px-4"><ReasonBadge reason={row.reason} /></td>
      <td className="py-3 px-4 text-right font-mono text-xs font-semibold">{fmt(row.total_notice_salary)}</td>
      <td className="py-3 px-4 text-right font-mono text-xs text-emerald-600">+{fmt(row.reward)}</td>
      <td className="py-3 px-4 text-right font-mono text-xs text-emerald-600">+{fmt(row.retention)}</td>
      <td className="py-3 px-4 text-right font-mono text-xs text-red-600">-{fmt(row.recovery)}</td>
      <td className="py-3 px-4 text-right font-mono text-xs text-red-600">-{fmt(row.income_tax)}</td>
      <td className="py-3 px-4 text-right">
        <span className={`font-mono font-bold text-sm ${isNegative ? 'text-red-600' : 'text-emerald-700'}`}>
          {isNegative ? '-' : ''}{fmt(Math.abs(row.bank_transfer))}
        </span>
      </td>
      <td className="py-3 px-4 text-center">
        <StatusBadge status={row.status} isFinalized={row.is_finalized} isPaid={row.is_paid} />
      </td>
      <td className="py-3 px-4 text-xs text-muted-foreground">{row.finalized_date || '—'}</td>
    </tr>
  );
}

function SummaryRow({ records }) {
  let totalNoticeSalary = 0, totalReward = 0, totalRetention = 0, totalRecovery = 0, totalIT = 0, totalBank = 0;
  for (let i = 0; i < records.length; i++) {
    totalNoticeSalary += records[i].total_notice_salary || 0;
    totalReward += records[i].reward || 0;
    totalRetention += records[i].retention || 0;
    totalRecovery += records[i].recovery || 0;
    totalIT += records[i].income_tax || 0;
    totalBank += records[i].bank_transfer || 0;
  }
  return (
    <tr className="bg-muted/50 font-semibold text-sm border-t-2">
      <td className="py-3 px-4" colSpan={2}>Totals ({records.length} employees)</td>
      <td colSpan={4}></td>
      <td className="py-3 px-4 text-right font-mono">{fmt(totalNoticeSalary)}</td>
      <td className="py-3 px-4 text-right font-mono text-emerald-600">+{fmt(totalReward)}</td>
      <td className="py-3 px-4 text-right font-mono text-emerald-600">+{fmt(totalRetention)}</td>
      <td className="py-3 px-4 text-right font-mono text-red-600">-{fmt(totalRecovery)}</td>
      <td className="py-3 px-4 text-right font-mono text-red-600">-{fmt(totalIT)}</td>
      <td className="py-3 px-4 text-right font-mono font-bold">{fmt(totalBank)}</td>
      <td colSpan={2}></td>
    </tr>
  );
}

function exportCSV(records) {
  const headers = ['Employee', 'Emp ID', 'Organization', 'Notice Date', 'LWD', 'Days Worked', 'Total Days', 'Reason', 'Total Notice Salary', 'Reward', 'Retention', 'Recovery', 'Income Tax', 'Bank Transfer', 'Status', 'Finalized Date'];
  const rows = [];
  for (let i = 0; i < records.length; i++) {
    const r = records[i];
    rows.push([r.name, r.emp_code, r.organization, r.notice_date, r.lwd, r.days_worked, r.total_days, r.reason, r.total_notice_salary, r.reward, r.retention, r.recovery, r.income_tax, r.bank_transfer, r.status, r.finalized_date].join(','));
  }
  const csv = headers.join(',') + '\n' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'settlement_history.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function SettlementHistory() {
  const [records, setRecords] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterOrg, setFilterOrg] = useState('all');
  const [filterReason, setFilterReason] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [totals, setTotals] = useState({ count: 0, total_paid_out: 0, total_recovered: 0 });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterOrg !== 'all') params.organization_id = filterOrg;
      if (filterReason !== 'all') params.reason = filterReason;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (search.trim()) params.search = search.trim();
      const res = await salaryAPI.getSettlementHistory(params);
      setRecords(res.data?.records || []);
      setTotals(res.data?.totals || { count: 0, total_paid_out: 0, total_recovered: 0 });
    } catch {
      toast.error('Failed to load settlement history');
    } finally {
      setLoading(false);
    }
  }, [filterOrg, filterReason, fromDate, toDate, search]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    organizationAPI.getAll().then(r => setOrgs(r.data || [])).catch(() => {});
  }, []);

  const tableRows = [];
  for (let i = 0; i < records.length; i++) {
    tableRows.push(<HistoryTableRow key={records[i].employee_id} row={records[i]} />);
  }

  const orgOptions = [];
  for (let i = 0; i < orgs.length; i++) {
    orgOptions.push(<SelectItem key={orgs[i].id} value={orgs[i].id}>{orgs[i].name}</SelectItem>);
  }

  return (
    <div className="space-y-5">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KPICard
          icon={<Users className="h-5 w-5 text-orange-600" />}
          label="Total Settlements"
          value={totals.count}
          color="bg-orange-100 dark:bg-orange-900/30"
        />
        <KPICard
          icon={<Banknote className="h-5 w-5 text-emerald-600" />}
          label="Total Paid Out"
          value={fmt(totals.total_paid_out)}
          color="bg-emerald-100 dark:bg-emerald-900/30"
        />
        <KPICard
          icon={<TrendingDown className="h-5 w-5 text-red-600" />}
          label="Total Recovered"
          value={fmt(totals.total_recovered)}
          color="bg-red-100 dark:bg-red-900/30"
        />
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or ID..."
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="history-search"
          />
        </div>
        <Select value={filterOrg} onValueChange={setFilterOrg}>
          <SelectTrigger className="w-[180px]" data-testid="history-org-filter">
            <SelectValue placeholder="All Organizations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Organizations</SelectItem>
            {orgOptions}
          </SelectContent>
        </Select>
        <Select value={filterReason} onValueChange={setFilterReason}>
          <SelectTrigger className="w-[140px]" data-testid="history-reason-filter">
            <SelectValue placeholder="All Reasons" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Reasons</SelectItem>
            <SelectItem value="resignation">Resignation</SelectItem>
            <SelectItem value="termination">Termination</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" />
          <Input type="date" className="w-[140px]" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="history-from-date" />
          <span>to</span>
          <Input type="date" className="w-[140px]" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="history-to-date" />
        </div>
        <div className="ml-auto">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => exportCSV(records)} data-testid="history-export-btn">
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading settlement history...
        </div>
      ) : records.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <Users className="h-14 w-14 mb-3 opacity-30" />
            <p className="text-lg font-medium">No settlement records found</p>
            <p className="text-sm">Finalized and paid settlements will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="history-table">
              <thead>
                <tr className="border-b bg-muted/40 text-xs text-muted-foreground uppercase tracking-wider">
                  <th className="py-3 px-4 font-semibold">Employee</th>
                  <th className="py-3 px-4 font-semibold">Organization</th>
                  <th className="py-3 px-4 font-semibold">Notice Date</th>
                  <th className="py-3 px-4 font-semibold">LWD</th>
                  <th className="py-3 px-4 font-semibold text-center">Days</th>
                  <th className="py-3 px-4 font-semibold">Reason</th>
                  <th className="py-3 px-4 font-semibold text-right">Total Notice Salary</th>
                  <th className="py-3 px-4 font-semibold text-right">+ Reward</th>
                  <th className="py-3 px-4 font-semibold text-right">+ Retention</th>
                  <th className="py-3 px-4 font-semibold text-right">- Recovery</th>
                  <th className="py-3 px-4 font-semibold text-right">- IT</th>
                  <th className="py-3 px-4 font-semibold text-right">= Bank Transfer</th>
                  <th className="py-3 px-4 font-semibold text-center">Status</th>
                  <th className="py-3 px-4 font-semibold">Finalized</th>
                </tr>
              </thead>
              <tbody>
                {tableRows}
                <SummaryRow records={records} />
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
